<template>
    <v-card flat class="pa-2">

        <!--Summary Chips-->
        <v-layout row wrap align-center class="pb-1">
            <v-flex shrink class="pr-2 pb-1">
                <v-chip small color="green" text-color="white">
                    <v-icon small left>create_new_folder</v-icon>
                    {{ summary.foldersNew }} new folders
                </v-chip>
            </v-flex>
            <v-flex shrink class="pr-2 pb-1">
                <v-chip small color="blue" text-color="white">
                    <v-icon small left>call_merge</v-icon>
                    {{ summary.foldersMatched }} matched
                </v-chip>
            </v-flex>
            <v-flex shrink class="pr-2 pb-1">
                <v-chip small color="orange" text-color="white">
                    <v-icon small left>help_outline</v-icon>
                    {{ summary.foldersAmbiguous }} ambiguous
                </v-chip>
            </v-flex>
            <v-flex shrink class="pr-2 pb-1">
                <v-chip small :color="summary.rowErrors > 0 ? 'red' : 'grey'" text-color="white">
                    <v-icon small left>error_outline</v-icon>
                    {{ summary.rowErrors }} row errors
                </v-chip>
            </v-flex>
        </v-layout>

        <!--Bulk Actions-->
        <v-layout row wrap class="pb-1">
            <v-flex shrink class="pr-2">
                <v-btn
                        small
                        flat
                        color="secondary"
                        class="ma-0"
                        :disabled="busy"
                        @click="$emit('bulk-action', {matchState: 'new', action: 'create'})"
                >
                    <v-icon left small>create_new_folder</v-icon>
                    Create All New
                </v-btn>
            </v-flex>
            <v-flex shrink class="pr-2">
                <v-btn
                        small
                        flat
                        color="secondary"
                        class="ma-0"
                        :disabled="busy"
                        @click="$emit('bulk-action', {matchState: 'matched', action: 'merge'})"
                >
                    <v-icon left small>call_merge</v-icon>
                    Merge All Matched
                </v-btn>
            </v-flex>
            <v-flex shrink class="pr-2">
                <v-btn
                        small
                        flat
                        color="secondary"
                        class="ma-0"
                        :disabled="busy"
                        @click="$emit('bulk-action', {matchState: 'matched', action: 'skip'})"
                >
                    <v-icon left small>skip_next</v-icon>
                    Skip All Matched
                </v-btn>
            </v-flex>
        </v-layout>

        <!--Folder Table-->
        <v-data-table
                :headers="tableHeaders"
                :items="preview.folders"
                item-key="id"
                class="elevation-1 compact-table"
                :rows-per-page-items="[10, 25, 50, {text: 'All', value: -1}]"
                :loading="busy"
        >
            <template slot="items" slot-scope="props">
                <td>
                    {{ props.item.display_name }}
                    <v-tooltip top v-if="props.item.sameNameOtherGroup">
                        <v-icon small color="orange" slot="activator">info</v-icon>
                        <span>A folder with the same name exists in another folder group</span>
                    </v-tooltip>
                </td>
                <td class="text-xs-right">{{ props.item.rowCount }}</td>
                <td>
                    <v-chip small :color="matchColor(props.item.match_state)" text-color="white">
                        {{ props.item.match_state }}
                    </v-chip>
                </td>
                <td style="min-width: 140px">
                    <v-select
                            dense
                            :items="actionItems"
                            :value="props.item.action"
                            placeholder="Select action"
                            hide-details
                            single-line
                            color="primary"
                            class="ma-0 pa-0"
                            @input="onActionChange(props.item, $event)"
                    ></v-select>
                </td>
                <td style="min-width: 200px">
                    <template v-if="props.item.match_state == 'ambiguous'">
                        <v-autocomplete
                                dense
                                :items="props.item.candidates != null ? props.item.candidates : []"
                                item-text="description"
                                item-value="id"
                                :value="props.item.fk_matched_folder"
                                placeholder="Select target folder"
                                hide-details
                                single-line
                                color="primary"
                                class="ma-0 pa-0"
                                @input="onTargetChange(props.item, $event)"
                        ></v-autocomplete>
                    </template>
                    <template v-else-if="props.item.match_state == 'matched'">
                        <span class="body-1">Folder #{{ props.item.fk_matched_folder }}</span>
                    </template>
                    <template v-else>
                        <span class="body-1 greyType--text">&mdash;</span>
                    </template>
                </td>
            </template>
        </v-data-table>

        <!--Row Errors-->
        <template v-if="rowErrors.length > 0">
            <v-layout row align-center class="pt-2">
                <v-flex shrink>
                    <span class="caption red--text">Row Errors ({{ rowErrors.length }})</span>
                </v-flex>
                <v-flex shrink class="pl-2">
                    <v-btn small flat icon color="red" class="ma-0 pa-0" @click="showRowErrors = !showRowErrors">
                        <v-icon small>{{ showRowErrors ? 'expand_less' : 'expand_more' }}</v-icon>
                    </v-btn>
                </v-flex>
            </v-layout>
            <div class="caption greyType--text">These rows will not be imported</div>
            <v-data-table
                    v-if="showRowErrors"
                    :headers="rowErrorHeaders"
                    :items="rowErrors"
                    class="elevation-1 mt-1 compact-table"
                    :rows-per-page-items="[10, 25, {text: 'All', value: -1}]"
            >
                <template slot="items" slot-scope="props">
                    <td class="text-xs-right" style="width: 100px">{{ props.item.row_index }}</td>
                    <td>{{ props.item.error }}</td>
                </template>
            </v-data-table>
        </template>

        <!--Actions-->
        <v-layout row align-center class="pt-2">
            <v-flex shrink>
                <v-btn small flat color="grey" class="ma-0" :disabled="busy" @click="$emit('back')">
                    <v-icon small left>chevron_left</v-icon>
                    Back
                </v-btn>
            </v-flex>
            <v-flex shrink class="pl-2">
                <v-tooltip top :disabled="!executeBlocked">
                    <div slot="activator" style="display: inline-block">
                        <v-btn
                                color="secondary"
                                class="ma-0"
                                :disabled="executeBlocked || busy"
                                :loading="busy"
                                @click="$emit('execute')"
                        >
                            <v-icon left>play_arrow</v-icon>
                            Execute Import
                        </v-btn>
                    </div>
                    <span>Resolve all ambiguous folders first - each one needs an action, and merges need a target folder</span>
                </v-tooltip>
            </v-flex>
        </v-layout>
    </v-card>
</template>

<script>

    import {codeBaseMixin} from "../../codeBaseMixin";

    export default {
        mixins: [codeBaseMixin],
        props: {
            preview: Object,
            busy: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                showRowErrors: false,
                tableHeaders: [
                    {text: 'Folder', value: 'display_name'},
                    {text: 'Rows', value: 'rowCount', align: 'right'},
                    {text: 'Match', value: 'match_state'},
                    {text: 'Action', value: 'action', sortable: false},
                    {text: 'Matched Folder', value: 'fk_matched_folder', sortable: false}
                ],
                rowErrorHeaders: [
                    {text: 'Row', value: 'row_index', align: 'right'},
                    {text: 'Error', value: 'error', sortable: false}
                ],
                actionItems: [
                    {text: 'Create', value: 'create'},
                    {text: 'Merge', value: 'merge'},
                    {text: 'Skip', value: 'skip'}
                ]
            }
        },
        computed: {
            summary() {
                return this.preview != null && this.preview.summary != null
                    ? this.preview.summary
                    : {foldersNew: 0, foldersMatched: 0, foldersAmbiguous: 0, rowErrors: 0};
            },
            rowErrors() {
                return this.preview != null && this.preview.rowErrors != null ? this.preview.rowErrors : [];
            },
            executeBlocked() {
                if (this.preview == null || this.preview.folders == null) {
                    return true;
                }
                return this.preview.folders
                    .filter(folder => folder.match_state == 'ambiguous')
                    .some(folder => folder.action == null || (folder.action == 'merge' && folder.fk_matched_folder == null));
            }
        },
        methods: {
            matchColor(matchState) {
                switch (matchState) {
                    case 'new':
                        return 'green';
                    case 'matched':
                        return 'blue';
                    case 'ambiguous':
                        return 'orange';
                    default:
                        return 'grey';
                }
            },
            onActionChange(folder, action) {
                if (action == null || action == folder.action) {
                    return;
                }
                this.$emit('folder-action', {
                    folderId: folder.id,
                    action: action,
                    fkMatchedFolder: folder.fk_matched_folder
                });
            },
            onTargetChange(folder, targetId) {
                if (targetId == null || targetId == folder.fk_matched_folder) {
                    return;
                }
                this.$emit('folder-action', {
                    folderId: folder.id,
                    action: folder.action != null ? folder.action : 'merge',
                    fkMatchedFolder: targetId
                });
            }
        }
    }
</script>

<style scoped>
    .compact-table >>> td, .compact-table >>> th {
        padding: 0 8px !important;
        height: 32px !important;
    }
</style>
